import React, { useCallback, useEffect, useState } from "react";

import { ArrowLeft, Loader2, Target } from "lucide-react";

import {
  ASSESSMENT_LEVEL_RL,
  buildRlLevelLookupMaps,
  formatRlGoalLabel,
} from "./organizationLevelUtils";
import {
  clampLevel,
  formatGoalHorizonLabel,
  formatLevel,
  getRlBadgeClasses,
  normalizePosture,
  resolvePairAssessmentUserId,
  resolvePairContactId,
} from "./relationshipUtils";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  fetchRelationshipAssessmentHistory,
  fetchRelationshipAssessmentLevels,
} from "../../../Store/Reducers/RelationshipSlice";

const POSTURE_LABELS = {
  unknown: "Unknown",
  advocate: "Advocate",
  neutral: "Neutral",
  detractor: "Detractor",
};

function formatHistoryDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatReassessDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function assessmentTypeLabel(entry, currentLabel = "Current") {
  if (entry.isCurrentPrimary) return currentLabel;
  return "History";
}

function goalTargetLabel(targetScore, rlLevelNames) {
  const rounded = clampLevel(targetScore);
  const dynamic = formatRlGoalLabel(rounded, rlLevelNames);
  if (dynamic && rlLevelNames?.[rounded]) return dynamic;
  return formatLevel("RL", rounded);
}

function HistoryGoalBlock({ entry, rlLevelNames }) {
  const target = entry.targetRlScore;
  if (target === null || target === undefined) return null;

  const currentRl = entry.rlScore;
  const targetRounded = clampLevel(target);
  const horizonLabel = formatGoalHorizonLabel(
    entry.goalTargetHorizon,
    entry.createdAt
  );
  const reassessLabel = formatReassessDate(entry.reassessBy);
  const hasHorizon = Boolean(entry.goalTargetHorizon && horizonLabel);

  return (
    <section className="mt-3 rounded-lg border border-amber-200/80 bg-gradient-to-br from-amber-50 to-orange-50/60 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-800">
        <Target className="h-3.5 w-3.5" aria-hidden />
        Relationship goal
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {currentRl !== null && currentRl !== undefined ? (
          <span
            className={`rounded-md px-2 py-1 text-xs font-bold ${getRlBadgeClasses(clampLevel(currentRl))}`}
          >
            {formatLevel("RL", clampLevel(currentRl))}
          </span>
        ) : null}
        {currentRl !== null &&
        currentRl !== undefined &&
        targetRounded !== null ? (
          <span className="text-xs font-semibold text-amber-700" aria-hidden>
            →
          </span>
        ) : null}
        <span
          className={`rounded-md px-2 py-1 text-xs font-bold ring-2 ring-amber-400/50 ${getRlBadgeClasses(targetRounded)}`}
        >
          {goalTargetLabel(target, rlLevelNames)}
        </span>
      </div>
      <p className="mt-2 text-xs font-medium text-amber-900">
        Target:{" "}
        <span className="font-semibold">
          {goalTargetLabel(target, rlLevelNames)}
        </span>
        {Number(target) !== targetRounded
          ? ` (${Number(target).toFixed(1)})`
          : ""}
      </p>
      {hasHorizon ? (
        <p className="mt-1.5 text-xs text-amber-800/90">
          <span className="font-semibold">Achieve within:</span> {horizonLabel}
        </p>
      ) : null}
      {reassessLabel ? (
        <p className="mt-1 text-xs text-amber-700/80">
          <span className="font-semibold">Reassess by:</span> {reassessLabel}
        </p>
      ) : null}
    </section>
  );
}

function HistoryRationaleBlock({ rationale }) {
  const text = (rationale || "").trim();
  if (!text) return null;

  return (
    <section className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
        Rationale
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-gray-700">{text}</p>
    </section>
  );
}

export function HistoryEntryCard({
  entry,
  rlLevelNames,
  currentLabel = "Current",
}) {
  const rl = entry.rlScore;
  const rlRounded = rl !== null && rl !== undefined ? clampLevel(rl) : null;
  const posture = normalizePosture(entry.assessedPosture);

  return (
    <li
      className={`rounded-xl border p-4 text-sm shadow-sm ${
        entry.isCurrentPrimary
          ? "border-purple-300 bg-purple-50/90"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {rlRounded !== null ? (
            <span
              className={`inline-flex rounded-md px-2.5 py-1 text-xs font-bold ${getRlBadgeClasses(rlRounded)}`}
            >
              {formatLevel("RL", rlRounded)}
              {rl !== rlRounded ? ` (${Number(rl).toFixed(1)})` : ""}
            </span>
          ) : (
            <span className="text-xs font-semibold text-gray-500">
              No RL score
            </span>
          )}
          <span
            className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
              entry.isCurrentPrimary
                ? "bg-purple-600 text-white"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {assessmentTypeLabel(entry, currentLabel)}
          </span>
        </div>
        <time className="shrink-0 text-xs font-medium text-gray-500">
          {formatHistoryDate(entry.createdAt)}
        </time>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
        <span>
          <span className="font-semibold text-gray-800">Assessor:</span>{" "}
          {entry.assessorName}
        </span>
        <span className="text-gray-300" aria-hidden>
          |
        </span>
        <span>
          <span className="font-semibold text-gray-800">Posture:</span>{" "}
          {POSTURE_LABELS[posture] || posture}
        </span>
      </div>

      <HistoryGoalBlock entry={entry} rlLevelNames={rlLevelNames} />
      <HistoryRationaleBlock rationale={entry.rationale} />
    </li>
  );
}

export function RlAssessmentHistoryPanel({
  accountId,
  userId,
  contactId,
  pair = null,
  stakeholder = null,
  contact = null,
  executiveId = null,
  stakeholderName,
  contactName,
  onBack,
  refreshKey = 0,
}) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [rlLevelNames, setRlLevelNames] = useState({});

  useEffect(() => {
    let cancelled = false;
    dispatch(fetchRelationshipAssessmentLevels(ASSESSMENT_LEVEL_RL))
      .unwrap()
      .then(levels => {
        if (cancelled) return;
        const { namesByLevel } = buildRlLevelLookupMaps(levels);
        setRlLevelNames(namesByLevel);
      })
      .catch(err => {
        console.error("Failed to load RL level names:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const loadHistory = useCallback(async () => {
    const resolvedUserId = resolvePairAssessmentUserId(
      pair,
      stakeholder,
      userId || executiveId
    );
    const resolvedContactId =
      resolvePairContactId(pair, contact) ||
      (contactId ? String(contactId) : null);
    const resolvedAccountId = accountId || pair?.accountId || null;

    if (!resolvedAccountId || !resolvedUserId || !resolvedContactId) {
      setLoading(false);
      setError("Unable to load history — missing pair identifiers.");
      setAssessments([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const rows = await dispatch(
        fetchRelationshipAssessmentHistory({
          accountId: resolvedAccountId,
          userId: resolvedUserId,
          contactId: resolvedContactId,
        })
      ).unwrap();
      setAssessments(rows);
    } catch (err) {
      console.error("Failed to load RL history:", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load assessment history."
      );
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [
    accountId,
    contact,
    contactId,
    dispatch,
    executiveId,
    pair,
    stakeholder,
    userId,
  ]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Back to assessment"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-900">RL History</h2>
            <p className="text-xs text-gray-500">
              {stakeholderName}
              {" → "}
              {contactName}
            </p>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-5">
        {loading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading history…
          </div>
        ) : null}

        {!loading && error ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}

        {!loading && !error && assessments.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-center text-sm text-gray-500">
            No RL assessments recorded for this pair yet.
          </div>
        ) : null}

        {!loading && !error && assessments.length > 0 ? (
          <ul className="space-y-4">
            {assessments.map(entry => (
              <HistoryEntryCard
                key={entry.id}
                entry={entry}
                rlLevelNames={rlLevelNames}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export default RlAssessmentHistoryPanel;
